import { useState } from 'react';
import { NJButton, NJHeading, NJTab, NJTabs, NJText } from '@engie-group/fluid-design-system-react';
import { UMIcon } from '../../urbanmap/_components';
import { PlanSegmentedControl } from './PlanSegmentedControl';
import { PlanSummary } from './PlanMetrics';
import { MODE_LABELS, PLAN_STEPS } from './plans.data';
import { useUpdatePlan } from './plans.hooks';
import type { DefinitionMode, DevelopmentPlan, PlanStepId } from './plans.types';
import { UMR } from './tokens';

interface Props {
  projectId: string;
  plan: DevelopmentPlan;
  onBack: () => void;
  flash: (message: string) => void;
}

const MODES: DefinitionMode[] = ['manual', 'algorithm'];
const STATUS = { done: 'Validée', current: 'À définir', locked: 'Verrouillée' } as const;
const TAB_LABELS: Record<PlanStepId, string> = { buildings: 'Bâtiments', route: 'Tracé', phasing: 'Phasage' };

const firstOpen = (plan: DevelopmentPlan): PlanStepId =>
  PLAN_STEPS[Math.min(plan.completedSteps, PLAN_STEPS.length - 1)].id;

/**
 * Étapes dans l'ordre : bâtiments, tracé, phasage. Une étape s'ouvre quand la précédente est validée ;
 * une étape validée reste modifiable ensuite.
 */
export function PlanStepsEditor({ projectId, plan, onBack, flash }: Props) {
  const update = useUpdatePlan(projectId);
  const [open, setOpen] = useState<PlanStepId>(() => firstOpen(plan));
  const done = plan.completedSteps;

  const validate = (index: number) => update.mutate({ planId: plan.id, patch: { completedSteps: index + 1 } }, {
    onSuccess: () => {
      const next = PLAN_STEPS[index + 1];
      if (next) setOpen(next.id);
      flash(next ? `${PLAN_STEPS[index].label} validé · étape suivante : ${next.label}` : `${plan.name} complet`);
    },
    onError: () => flash('Échec de l’enregistrement, réessayez'),
  });
  const setMode = (step: 'route' | 'phasing', mode: DefinitionMode) =>
    update.mutate({ planId: plan.id, patch: step === 'route' ? { routeMode: mode } : { phasingMode: mode } });

  return (
    <div className="pnv2-dp-editor">
      <NJButton label="Tous les plans" icon="arrow_back" variant="secondary" emphasis="minimal" scale="sm" onClick={onBack} />
      <div className="pnv2-dp-title">
        <NJHeading as="h3" scale="xs">{plan.name}</NJHeading>
        <NJText scale="xs" className="pnv2-dp-muted">
          {done >= PLAN_STEPS.length ? 'Plan complet' : `${done} étape${done > 1 ? 's' : ''} validée${done > 1 ? 's' : ''} sur 3`} · modifié le {plan.updatedAt}
        </NJText>
        <PlanSummary plan={plan} />
      </div>
      <NJTabs key={open} label={`Étapes du plan ${plan.name}`} activeTab={open} density="stretched" className="pnv2-dp-tabs"
        onClickTabItem={(id) => setOpen(id as PlanStepId)}>
        {PLAN_STEPS.map((step, index) => {
          const state = index < done ? 'done' : index === done ? 'current' : 'locked';
          const mode = step.id === 'route' ? plan.routeMode : plan.phasingMode;
          const panelId = `pnv2-dp-${plan.id}-${step.id}`;
          return (
            <NJTab key={step.id} id={step.id} label={`${state === 'done' ? '✓' : `${index + 1}.`} ${TAB_LABELS[step.id]}`} ariaControls={panelId}
              isDisabled={state === 'locked'} title={state === 'locked' ? `Disponible après « ${PLAN_STEPS[index - 1].label} »` : undefined}>
              <div className="pnv2-dp-stepbody" id={panelId}>
                <div className={`pnv2-dp-status pnv2-dp-step--${state}`}>
                  <span className="pnv2-dp-marker" aria-hidden="true">
                    {state === 'done' ? <UMIcon name="check" size={16} color={UMR.white} /> : index + 1}
                  </span>
                  <span className="pnv2-dp-item-text">
                    <NJText as="strong" scale="sm">{step.label}</NJText>
                    <NJText as="span" scale="xs" className="pnv2-dp-muted">
                      {STATUS[state]}{step.id !== 'buildings' ? ` · ${MODE_LABELS[mode]}` : ''}
                    </NJText>
                  </span>
                </div>
                <NJText scale="sm">{step.hint}</NJText>
                {step.id === 'buildings' && done >= 2 && (
                  <div className="pnv2-dp-note" role="note">
                    <UMIcon name="info" size={18} color={UMR.textSecondary} />
                    <NJText scale="xs">
                      Ajouter un bâtiment ajoute seulement son raccordement au tracé ; retirer un bâtiment supprime
                      seulement son raccordement. Le reste du tracé et du phasage est conservé.
                      {done >= 3 && ' Un bâtiment ajouté reste sans phase tant que vous ne lui en attribuez pas.'}
                    </NJText>
                  </div>
                )}
                {step.id !== 'buildings' && (
                  <PlanSegmentedControl label={`Mode de définition · ${step.label}`} options={MODES.map((m) => MODE_LABELS[m])}
                    value={MODE_LABELS[mode]}
                    onChange={(v) => setMode(step.id === 'route' ? 'route' : 'phasing', v === MODE_LABELS.algorithm ? 'algorithm' : 'manual')} />
                )}
                <div className="pnv2-dp-placeholder">
                  <NJText scale="sm">
                    {step.id === 'buildings' ? 'Sélection des bâtiments sur la carte et en liste'
                      : mode === 'manual' ? `Outils de ${step.label.toLowerCase()} manuel`
                        : `Paramètres et lancement de l’algorithme · ${step.label.toLowerCase()}`}
                  </NJText>
                  <NJText scale="xs" className="pnv2-dp-muted">Contenu à détailler</NJText>
                </div>
                {state === 'current' ? (
                  <NJButton label={update.isPending ? 'Enregistrement…' : step.validate} scale="sm" disabled={update.isPending}
                    onClick={() => validate(index)} />
                ) : (
                  <NJText scale="xs" className="pnv2-dp-muted">Étape validée · vous pouvez encore la modifier.</NJText>
                )}
              </div>
            </NJTab>
          );
        })}
      </NJTabs>
    </div>
  );
}
